import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { cancelSpecialOrder } from "../../services";
import { localizeApiMessage } from "../../errors/messages";
import { SpecialOrderDetail } from "../../types/special-order.type";
import { apiErrorMessage } from "./data";

interface CancelDialogProps {
  open: boolean;
  order: SpecialOrderDetail;
  onClose: () => void;
  onCancelled: () => void;
}

const FALLBACK_ERROR = "ยกเลิกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/**
 * Cancel an order. Before delivery nothing has touched stock, so it simply stops; once
 * delivered it cancels the BILL (stock reverted) — and the server may refuse that (409),
 * e.g. while the bill's e-Tax document is live. The refusal is shown here, in the dialog,
 * rather than as a toast behind it.
 */
export default function CancelDialog({ open, order, onClose, onCancelled }: CancelDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const isBill = order.status === "delivered";

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await cancelSpecialOrder(order.uuid, reason.trim() ? { reason: reason.trim() } : {});
      onCancelled();
    } catch (err) {
      const message = apiErrorMessage(err);
      setError(message ? localizeApiMessage(message) : FALLBACK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isBill ? "ยกเลิกบิล" : "ยกเลิกออเดอร์"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          {isBill
            ? `ยกเลิกบิล ${order.reserved_transaction_no ?? ""} ของออเดอร์ ${order.order_no} ระบบจะคืนสต็อกเข้าสาย ` +
              "ถ้าต้องการส่งใหม่ให้ออกออเดอร์ใหม่"
            : `ยกเลิกออเดอร์ ${order.order_no} ยังไม่มีการตัดสต็อก`}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="เหตุผล (ไม่บังคับ)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          ปิด
        </Button>
        <Button variant="contained" color="error" loading={loading} onClick={handleConfirm}>
          {isBill ? "ยืนยันยกเลิกบิล" : "ยืนยันยกเลิก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
